import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import ApiServices from "../services/ApiServices";
import React from "react";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";
import { ShowSuccessToast, ShowDangerToast } from "../services/toast";
import { Phone, Mail, MapPin, Building2, RefreshCw } from "lucide-react";

const calendarColors = {
  lightBackground: "#F7F8FA",
  mustardBrown: "#C19A6B",
  eventColors: [
    "#E6F7FF",
    "#FFFBE6",
    "#F6FFED",
    "#FFF1F0",
    "#F0FDF4",
    "#FEF2F2",
    "#F3E8FF",
  ],
};

const statusStyles = {
  pending: { background: "rgba(14, 165, 233, 0.2)", border: "2px solid rgba(14, 165, 233, 0.5)" },
  completed: { background: "rgba(22, 163, 74, 0.2)", border: "2px solid rgba(22, 163, 74, 0.5)" },
  assigned: { background: "rgba(29, 78, 216, 0.2)", border: "2px solid rgba(29, 78, 216, 0.5)" },
  rejected: { background: "rgba(249, 115, 22, 0.2)", border: "2px solid rgba(249, 115, 22, 0.5)" },
};

const statusStylesMonth = {
  pending: { background: "rgba(14, 165, 233, 0.1)", border: "2px solid rgba(14, 165, 233, 0.3)" },
  completed: { background: "rgba(22, 163, 74, 0.1)", border: "2px solid rgba(22, 163, 74, 0.3)" },
  assigned: { background: "rgba(29, 78, 216, 0.1)", border: "2px solid rgba(29, 78, 216, 0.3)" },
  rejected: { background: "rgba(249, 115, 22, 0.1)", border: "2px solid rgba(249, 115, 22, 0.3)" },
};

const monthEventColors = [
  "#0CC5DF",
  "#FFBF56",
  "#6F381E",
  "#49B25D",
  "#7D3AEF",
  "#FF268E",
];

const getColor = (idx) => monthEventColors[idx % monthEventColors.length];

const sanitizeDate = (dateStr) => dateStr?.split("T")[0];

const Calendar = ({ profile, profileImage, storeName, store }) => {
  const { storeId } = useParams();
  const [donationEvents, setDonationEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [fetchFrom, setFetchFrom] = useState(() => {
    const today = new Date();
    const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    return oneMonthAgo.toISOString().split("T")[0];
  });
  const [fetchTo, setFetchTo] = useState(() => new Date().toISOString().split("T")[0]);
  const [drivers, setDrivers] = useState([]);
  const [currentView, setCurrentView] = useState("week");
  const [statusFilter, setStatusFilter] = useState("all");
  const [popupEvents, setPopupEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loadingIds, setLoadingIds] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [pickupLatLng, setPickupLatLng] = useState({
    latitude: null,
    longitude: null,
  });

  // Manual Donation Form State
  const [showManualDonationForm, setShowManualDonationForm] = useState(false);
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);
  const [manualForm, setManualForm] = useState({
    donorName: "",
    donorPhone: "",
    donorEmail: "",
    pickupAddress: "",
    specialNote: "",
    businessName: "",
    pickUpType: "",
    pickUpTime: "AM",
    enableReminders: false,
    pickupDate: new Date().toISOString().split("T")[0],
    items: [{ name: "", description: "", image: null, imagePreview: null }],
    longitude: '',
    latitude: ''
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth < 1024;
  const BUFFER_DAYS = 31;

  const computeMinMaxForView = useCallback((date, view) => {
    let minD, maxD;
    switch (view) {
      case "month":
        const year = date.getFullYear();
        const month = date.getMonth();
        minD = new Date(year, month, 1).toISOString().split("T")[0];
        maxD = new Date(year, month + 1, 0).toISOString().split("T")[0];
        break;
      case "week":
        const weekDays = getWeekDays(date);
        minD = sanitizeDate(weekDays[0].toISOString());
        maxD = sanitizeDate(weekDays[6].toISOString());
        break;
      case "day":
        minD = maxD = sanitizeDate(date.toISOString());
        break;
      default:
        minD = maxD = sanitizeDate(date.toISOString());
    }
    return { minD, maxD };
  }, []);

  const updateDateRange = useCallback(() => {
    const { minD, maxD } = computeMinMaxForView(currentDate, currentView);
    let newFrom = fetchFrom;
    let newTo = fetchTo;
    const minDateObj = new Date(minD + "T00:00:00");
    const maxDateObj = new Date(maxD + "T23:59:59");
    const currentFromObj = new Date(fetchFrom + "T00:00:00");
    const currentToObj = new Date(fetchTo + "T23:59:59");
    if (minDateObj < currentFromObj) {
      const extendFromObj = new Date(minDateObj.getTime() - BUFFER_DAYS * 86400000);
      newFrom = extendFromObj.toISOString().split("T")[0];
    }
    if (maxDateObj > currentToObj) {
      const extendToObj = new Date(maxDateObj.getTime() + BUFFER_DAYS * 86400000);
      newTo = extendToObj.toISOString().split("T")[0];
    }
    if (newFrom !== fetchFrom || newTo !== fetchTo) {
      setFetchFrom(newFrom);
      setFetchTo(newTo);
      fetchDonationRequests(newFrom, newTo);
    }
  }, [currentDate, currentView, fetchFrom, fetchTo, computeMinMaxForView]);

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const handleManualDonationSubmit = async () => {
    // Validation
    if (!manualForm.donorName.trim()) {
      return ShowDangerToast("Donor name is required");
    }
    if (!manualForm.donorPhone.trim()) {
      return ShowDangerToast("Phone number is required");
    }
    if (!manualForm.pickupDate) {
      return ShowDangerToast("Please select a donation date");
    }
    if (manualForm.items.length === 0 || manualForm.items.every(i => !i.name.trim())) {
      return ShowDangerToast("At least one item with a name is required");
    }
    if (manualForm.items.some(item => item.name.trim() && !item.image)) {
      return ShowDangerToast("Every item with a name must have an image");
    }
    if (!pickupLatLng.latitude || !pickupLatLng.longitude) {
      return ShowDangerToast(
        "Please select a valid pickup address from the suggestions"
      );
    }


    setIsSubmittingManual(true);

    try {
      const formData = new FormData();

      // Required fields
      formData.append("storeId", storeId);
      formData.append("donorName", manualForm.donorName.trim());
      formData.append("phone", manualForm.donorPhone.trim());
      formData.append("address", manualForm.pickupAddress?.trim() || store?.address || "");
      formData.append("latitude", String(pickupLatLng.latitude));
      formData.append("longitude", String(pickupLatLng.longitude));

      formData.append("pickupDate", manualForm.pickupDate);
      formData.append("pickUpTime", manualForm.pickUpTime || "AM");
      formData.append("pickUpType", manualForm.pickUpType || "Corporate");
      formData.append(
        "enableReminders",
        manualForm.enableReminders ? true : false
      );


      // Optional fields
      formData.append("email", manualForm.donorEmail.trim() || "");
      formData.append("businessName", manualForm.businessName.trim() || "");
      formData.append("specialNote", manualForm.specialNote.trim() || "");

      // Append items with proper indexing
      manualForm.items.forEach((item, index) => {
        if (!item.name.trim()) return;

        formData.append("items", JSON.stringify({
          name: item.name.trim(),
          description: item.description?.trim() || "",
          index: index,
        }));

        if (item.image) {
          formData.append(`item-${item.name.trim()}-0`, item.image);
        }
      });

      await ApiServices.postFormData("/donations", formData);
      ShowSuccessToast("Manual donation added successfully!");

      // Reset form
      setShowManualDonationForm(false);
      setManualForm({
        donorName: "",
        donorPhone: "",
        donorEmail: "",
        pickupAddress: "",
        specialNote: "",
        businessName: "",
        pickUpType: "Walk-in",
        pickUpTime: "AM",
        pickupDate: new Date().toISOString().split("T")[0],
        items: [{ name: "", description: "", image: null, imagePreview: null }],
      });
      setAddressSuggestions([]);
      handleRefresh();
    } catch (err) {
      console.error("Manual donation submit error:", err);
      ShowDangerToast(
        "Failed to save donation: " +
        (err?.response?.data?.error || err.message || "Unknown error")
      );
    } finally {
      setIsSubmittingManual(false);
    }
  };
  const fetchDonationRequests = async (fromDate = fetchFrom, toDate = fetchTo) => {
    try {
      const res = await ApiServices.getDonationRequests(storeId, fromDate, toDate);
      const donations = res.data;
      const events = donations.map((donation, idx) => {
        const mainItem = donation.DonationItems?.[0];
        return {
          title: donation.donorName || "Unknown",
          start:
            sanitizeDate(donation.pickupDate) +
            (donation.pickupTime === "AM" ? "T09:00:00" : "T13:00:00"),
          allDay: false,
          extendedProps: {
            id: donation.id,
            donorName: donation.donorName || "Unknown",
            donorPhone: donation.donorPhone,
            driver: donation.driver?.fullName || "",
            driverId: donation.driver?.id || null,
            note: donation.specialNote || "",
            status: donation.status || "pending",
            address: donation.pickupAddress || donation.address || "No address",
            pickupTime: donation.pickupTime,
            donorImage: mainItem?.imageUrl || null,
            itemImage: mainItem?.imageUrl || null,
            email: donation.donorEmail || "",
            date: sanitizeDate(donation.pickupDate),
            item: mainItem?.name || "",
            description: mainItem?.description || "",
            donationItems: donation.DonationItems || [],
            colorIdx: idx % monthEventColors.length,
            pickUpType: donation.pickUpType || "N/A",
            businessName: donation.businessName || "",
            isMonetary: donation.isMonetary || false,
            monetaryAmount: donation.monetaryAmount || 0,
            paymentStatus: donation.paymentStatus || "N/A",
            paymentIntentId: donation.paymentIntentId || null,
          },
        };
      });
      setDonationEvents(events);
      // console.log(storeId, fromDate, toDate)
    } catch (err) {
      console.error("Failed to fetch donations", err);
      ShowDangerToast?.(err?.response?.data?.error || "Failed to fetch donations.");
    }
  };

  const handleRefresh = () => {
    fetchDonationRequests(fetchFrom, fetchTo);
  };

  useEffect(() => {
    (async () => {
      await fetchDonationRequests(fetchFrom, fetchTo);
      try {
        const resDrivers = await ApiServices.getDrivers(storeId);
        setDrivers(resDrivers);
      } catch (err) {
        console.error("Failed to fetch drivers", err);
        ShowDangerToast?.(err?.response?.data?.error || "Failed to fetch drivers.");
      }
    })();
  }, [storeId, fetchFrom, fetchTo]);

  useEffect(() => {
    updateDateRange();
  }, [currentDate, currentView, updateDateRange]);

  const handlePrev = () => {
    const copy = new Date(currentDate);
    if (currentView === "month") {
      copy.setMonth(copy.getMonth() - 1);
    } else {
      copy.setDate(copy.getDate() - (currentView === "day" ? 1 : 7));
    }
    setCurrentDate(copy);
  };

  const handleNext = () => {
    const copy = new Date(currentDate);
    if (currentView === "month") {
      copy.setMonth(copy.getMonth() + 1);
    } else {
      copy.setDate(copy.getDate() + (currentView === "day" ? 1 : 7));
    }
    setCurrentDate(copy);
  };

  const getWeekDays = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    const startDay = firstDay.getDay();
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dayStr = sanitizeDate(day.toISOString());
    return (
      donationEvents
        .filter((e) => {
          const matchesDate = sanitizeDate(e.start) === dayStr;
          const status = e.extendedProps.status || "pending";
          const matchesStatus =
            statusFilter === "all"
              ? status === "pending" || status === "assigned"
              : status === statusFilter;
          return matchesDate && matchesStatus;
        })
        .sort((a, b) => new Date(a.start) - new Date(b.start)) || []
    );
  };

  const openEventPopup = (event) => {
    setPopupEvents([event]);
    setSelectedEvent(event);
  };

  const openMorePopup = (events) => {
    setPopupEvents(events);
    setSelectedEvent(null);
  };

  // Responsive PopupContent component
  const PopupContent = ({ events, drivers }) => {
    const [localDriverMap, setLocalDriverMap] = useState({});
    const [localDateMap, setLocalDateMap] = useState({});
    const [localTimeMap, setLocalTimeMap] = useState({});
    const [loadingIdsLocal, setLoadingIdsLocal] = useState([]);
    const [imageIndexMap, setImageIndexMap] = useState({});
    const [rejectModal, setRejectModal] = useState({ open: false, requestId: null, rejectionReason: "" });
    const [rejectItemModal, setRejectItemModal] = useState({
      open: false,
      requestId: null,
      itemIds: [],
      rejectionReason: "",
      itemName: "",
    });
    const [undoModal, setUndoModal] = useState({ open: false, requestId: null });
    const [undoItemModal, setUndoItemModal] = useState({
      open: false,
      requestId: null,
      itemIds: [],
      itemName: "",
    });
    const [submittingItem, setSubmittingItem] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    React.useEffect(() => {
      let driverInit = {};
      let dateInit = {};
      let timeInit = {};
      let indexInit = {};
      events.forEach((e) => {
        driverInit[e.extendedProps.id] = e.extendedProps.driverId || "";
        dateInit[e.extendedProps.id] = e.extendedProps.date || "";
        timeInit[e.extendedProps.id] = e.extendedProps.pickupTime || "AM";
        indexInit[e.extendedProps.id] = 0;
      });
      setLocalDriverMap(driverInit);
      setLocalDateMap(dateInit);
      setLocalTimeMap(timeInit);
      setImageIndexMap(indexInit);
    }, [events]);
    const handleUpdate = async (event) => {
      const id = event.extendedProps.id;
      const driverId = localDriverMap[id];
      const pickupDate = localDateMap[id];
      const pickupTime = localTimeMap[id];
      setLoadingIdsLocal((ids) => [...ids, id]);
      try {
        let updatesMade = false;
        if (driverId && driverId !== event.extendedProps.driverId) {
          await ApiServices.assignDriver(id, driverId);
          updatesMade = true;
        }
        if (
          pickupDate &&
          (pickupDate !== event.extendedProps.date ||
            pickupTime !== event.extendedProps.pickupTime)
        ) {
          await ApiServices.postData(`/donations/${id}`, { pickupDate, pickupTime });
          updatesMade = true;
        }
        if (!updatesMade) {
          ShowDangerToast("No changes to update.");
          setLoadingIdsLocal((ids) => ids.filter((x) => x !== id));
          return;
        }
        setPopupEvents([]);
        await fetchDonationRequests(fetchFrom, fetchTo);
        ShowSuccessToast("Donation updated successfully");
      } catch (err) {
        console.error("Update error", err);
        ShowDangerToast?.(err?.response?.data?.error || "Failed to update donation.");
      } finally {
        setLoadingIdsLocal((ids) => ids.filter((x) => x !== id));
      }
    };
    const handleAssign = async (event) => {
      const id = event.extendedProps.id;
      const driverId = localDriverMap[id];
      const pickupDate = localDateMap[id];
      const pickupTime = localTimeMap[id];
      setLoadingIdsLocal((ids) => [...ids, id]);
      try {
        if (!driverId) {
          ShowDangerToast("Please select a driver.");
          setLoadingIdsLocal((ids) => ids.filter((x) => x !== id));
          return;
        }
        await ApiServices.assignDriver(id, driverId);
        if (
          pickupDate &&
          (pickupDate !== event.extendedProps.date ||
            pickupTime !== event.extendedProps.pickupTime)
        ) {
          await ApiServices.postData(`/donations/${id}`, { pickupDate, pickupTime });
        }
        setPopupEvents([]);
        await fetchDonationRequests(fetchFrom, fetchTo);
        ShowSuccessToast("Driver assigned successfully");
      } catch (err) {
        console.error("Assign error", err);
        ShowDangerToast?.(err?.response?.data?.error || "Failed to assign driver.");
      } finally {
        setLoadingIdsLocal((ids) => ids.filter((x) => x !== id));
      }
    };
    const submitReject = async () => {
      setIsRejecting(true);  // NEW: Start loading
      try {
        console.log("Rejecting donation request with ID:", rejectModal.requestId);
        console.log("Rejection reason:", rejectModal.rejectionReason);
        const response = await ApiServices.postData("/donations/reject", {
          requestId: rejectModal.requestId,
          rejectionReason: rejectModal.rejectionReason,
        });
        console.log("Reject API response:", response);
        ShowSuccessToast("Rejected successfully");
        setRejectModal({ open: false, requestId: null, rejectionReason: "" });
        setPopupEvents([]);
        setSelectedEvent(null);
        await fetchDonationRequests(fetchFrom, fetchTo);
      } catch (error) {
        console.error("Reject API error:", error);
        ShowDangerToast("Failed to reject donation");
      } finally {
        setIsRejecting(false);  // NEW: End loading
      }
    };
    const openRejectModal = (id) => {
      setRejectModal({ open: true, requestId: id, rejectionReason: "" });
    };
    const submitUndo = async () => {
      const id = undoModal.requestId;
      setLoadingIdsLocal((ids) => [...ids, id]);
      setIsUndoing(true);  // NEW: Start loading
      try {
        await ApiServices.postData(`/donations/${id}`, { status: "pending" });
        ShowSuccessToast("Undo reject successful");
        setUndoModal({ open: false, requestId: null });
        setPopupEvents([]);
        setSelectedEvent(null);
        await fetchDonationRequests(fetchFrom, fetchTo);
      } catch (error) {
        console.error("Undo reject error:", error);
        ShowDangerToast("Failed to undo reject");
      } finally {
        setLoadingIdsLocal((ids) => ids.filter((x) => x !== id));
        setIsUndoing(false);  // NEW: End loading
      }
    };
    const openUndoModal = (id) => {
      setUndoModal({ open: true, requestId: id });
    };
    const handleRejectItem = (requestId, itemIds, itemName, itemIndex) => {
      setRejectItemModal({
        open: true,
        requestId,
        itemIds,
        rejectionReason: "",
        itemName,
      });
      setImageIndexMap((prev) => ({ ...prev, [requestId]: itemIndex }));
      setSelectedItemId(itemIds[0]);
    };
    const submitRejectItem = async () => {
      const numItems = rejectItemModal.itemIds.length;
      if (!window.confirm(`Are you sure you want to reject ${numItems > 1 ? `these ${numItems} items` : 'this item'} "${rejectItemModal.itemName}"?`)) {
        return;
      }
      try {
        setSubmittingItem(true);
        await ApiServices.postData("/donations/updateDonationStatus", {
          requestId: rejectItemModal.requestId,
          items: JSON.stringify(
            rejectItemModal.itemIds.map((id) => ({
              id,
              status: "rejected",
              rejectionReason: rejectItemModal.rejectionReason || "No reason provided",
            }))
          ),
        });
        ShowSuccessToast(`Item "${rejectItemModal.itemName}" rejected successfully`);
        setRejectItemModal({ open: false, requestId: null, itemIds: [], rejectionReason: "", itemName: "" });
        setPopupEvents([]);
        setSelectedEvent(null);
        setSelectedItemId(null);
        await fetchDonationRequests(fetchFrom, fetchTo);
      } catch (error) {
        console.error("Failed to reject item:", error);
        ShowDangerToast("Failed to reject donation item");
      } finally {
        setSubmittingItem(false);
      }
    };
    const handleUndoItem = (requestId, itemIds, itemName, itemIndex) => {
      setUndoItemModal({
        open: true,
        requestId,
        itemIds,
        itemName,
      });
      setImageIndexMap((prev) => ({ ...prev, [requestId]: itemIndex }));
      setSelectedItemId(itemIds[0]);
    };
    const handleUndoRequest = async (requestId, donationItems) => {
      try {
        setLoadingIdsLocal((prev) => [...prev, requestId]);
        const updatedItems = donationItems.map((item) => ({
          id: item.id,
          status: "pending",
          rejectionReason: "",
        }));
        await ApiServices.postData("/donations/updateDonationStatus", {
          requestId,
          requestStatus: "pending",
          items: JSON.stringify(updatedItems),
        });
        ShowSuccessToast(`Undo reject for request #${requestId} successful`);
        await fetchDonationRequests(fetchFrom, fetchTo);
      } catch (error) {
        console.error("Undo reject request failed:", error);
        ShowDangerToast("Failed to undo reject donation request");
      } finally {
        setLoadingIdsLocal((prev) => prev.filter((id) => id !== requestId));
      }
    };
    const submitUndoItem = async () => {
      const numItems = undoItemModal.itemIds.length;
      if (!window.confirm(`Are you sure you want to undo reject for ${numItems > 1 ? `these ${numItems} items` : 'this item'} "${undoItemModal.itemName}"?`)) {
        return;
      }
      try {
        setSubmittingItem(true);
        await ApiServices.postData("/donations/updateDonationStatus", {
          requestId: undoItemModal.requestId,
          items: JSON.stringify(
            undoItemModal.itemIds.map((id) => ({
              id,
              status: "pending",
              rejectionReason: "",
            }))
          ),
        });
        ShowSuccessToast(`Undo reject for item "${undoItemModal.itemName}" successful`);
        setUndoItemModal({ open: false, requestId: null, itemIds: [], itemName: "" });
        setPopupEvents([]);
        setSelectedEvent(null);
        setSelectedItemId(null);
        await fetchDonationRequests(fetchFrom, fetchTo);
      } catch (error) {
        console.error("Failed to undo reject item:", error);
        ShowDangerToast("Failed to undo reject donation item");
      } finally {
        setSubmittingItem(false);
      }
    };
    const handlePrevImage = (id) => {
      setImageIndexMap((prev) => {
        const items = selectedEvent.extendedProps.donationItems;
        const newIndex = (prev[id] - 1 + items.length) % items.length;
        setSelectedItemId(items[newIndex].id);
        return { ...prev, [id]: newIndex };
      });
    };
    const handleNextImage = (id) => {
      setImageIndexMap((prev) => {
        const items = selectedEvent.extendedProps.donationItems;
        const newIndex = (prev[id] + 1) % items.length;
        setSelectedItemId(items[newIndex].id);
        return { ...prev, [id]: newIndex };
      });
    };
    const handleItemClick = (requestId, itemId, itemIndex) => {
      setImageIndexMap((prev) => ({ ...prev, [requestId]: itemIndex }));
      setSelectedItemId(itemId);
    };
    if (!events || events.length === 0) return null;
    return (
      <div
        style={{
          maxWidth: "100%",
          width: "100%",
          background: "#fff",
          borderRadius: isMobile ? 16 : 22,
          boxShadow: "0 8px 32px #ececec",
          overflow: "hidden",
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          userSelect: "none",
        }}
      >
        {events.length > 1 && !selectedEvent ? (
          <div style={{ padding: isMobile ? "16px" : "20px", maxHeight: 500, overflowY: "auto" }}>
            <h2 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, marginBottom: 16, color: "#1F2937" }}>
              All Events for the Day
            </h2>
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 10,
                overflowX: isMobile ? "hidden" : "auto",
                overflowY: isMobile ? "auto" : "hidden",
                paddingBottom: 10,
              }}
            >
              {events.map((event) => {
                const addressLines = (event.extendedProps.address || "No address")
                  .split(", ")
                  .map((line, idx) => (
                    <div
                      key={idx}
                      style={{
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {line}
                    </div>
                  ));
                return (
                  <div
                    key={event.extendedProps.id}
                    onClick={() => setSelectedEvent(event)}
                    style={{
                      background: statusStyles[event.extendedProps.status].background,
                      border: statusStyles[event.extendedProps.status].border,
                      borderRadius: 12,
                      padding: isMobile ? 16 : 12,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: isMobile ? "row" : "column",
                      alignItems: isMobile ? "flex-start" : "center",
                      gap: isMobile ? 12 : 0,
                      boxShadow: "0 3px 14px rgba(83,102,166,0.07)",
                      border: "1px solid #EDF0FA",
                      fontFamily: "'poppins',sans-serif",
                      transition: "box-shadow 0.3s ease",
                      width: isMobile ? "100%" : "auto",
                      marginBottom: isMobile ? 8 : 0,
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(83,102,166,0.15)")}
                    onMouseOut={(e) => (e.currentTarget.style.boxShadow = "0 3px 14px rgba(83,102,166,0.07)")}
                  >
                    {event.extendedProps.donorImage ? (
                      <img
                        src={event.extendedProps.donorImage}
                        alt="donor"
                        style={{
                          width: isMobile ? 60 : 90,
                          height: isMobile ? 60 : 68,
                          borderRadius: 10,
                          objectFit: "cover",
                          marginBottom: isMobile ? 0 : 8,
                          background: "#F5F6F7",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: isMobile ? 60 : 90,
                          height: isMobile ? 60 : 68,
                          borderRadius: 10,
                          background: "#F5F6F7",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: isMobile ? 0 : 8,
                          fontSize: isMobile ? 12 : 14,
                          color: "#8090B3",
                          fontWeight: 600,
                          textAlign: "center",
                          flexShrink: 0,
                        }}
                      >
                        No Image
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: isMobile ? 14 : 16,
                          color: "#2B3A67",
                          marginBottom: 4,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {event.title}
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? 12 : 14,
                          color: "#8090B3",
                          marginBottom: 3,
                          fontWeight: 600,
                        }}
                      >
                        {event.extendedProps.donorPhone}
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? 11 : 13,
                          color: "#8090B3",
                          marginBottom: 6,
                          fontWeight: 600,
                        }}
                      >
                        {event.extendedProps.pickupTime === "AM" ? "9:07 AM" : "1:07 PM"}
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? 11 : 12,
                          color: "#2B3A67",
                          lineHeight: 1.3,
                        }}
                      >
                        {addressLines}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          selectedEvent && (
            <>
              {events.length > 1 && (
                <button
                  onClick={() => setSelectedEvent(null)}
                  style={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    background: "rgba(0,0,0,0.5)",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    padding: "6px 12px",
                    cursor: "pointer",
                    zIndex: 10,
                    transition: "background-color 0.3s ease",
                    fontSize: isMobile ? 12 : 14,
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.7)")}
                  onMouseOut={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.5)")}
                >
                  Back to List
                </button>
              )}
              <div style={{ position: "relative" }}>
                {selectedEvent.extendedProps.donationItems[imageIndexMap[selectedEvent.extendedProps.id]]?.imageUrl ? (
                  <img
                    src={selectedEvent.extendedProps.donationItems[imageIndexMap[selectedEvent.extendedProps.id]]?.imageUrl}
                    alt="item"
                    style={{ width: "100%", height: isMobile ? 160 : 220, objectFit: "cover", borderRadius: "12px 12px 0 0" }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: isMobile ? 160 : 220,
                      borderRadius: "12px 12px 0 0",
                      background: "#F5F6F7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: isMobile ? 14 : 16,
                      color: "#8090B3",
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    No Image
                  </div>
                )}
                {selectedEvent.extendedProps.donationItems.length > 1 && (
                  <>
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: 10,
                        transform: "translateY(-50%)",
                        display: "flex",
                        gap: 10,
                      }}
                    >
                      <button
                        onClick={() => handlePrevImage(selectedEvent.extendedProps.id)}
                        style={{
                          background: "rgba(0,0,0,0.5)",
                          color: "white",
                          border: "none",
                          borderRadius: "50%",
                          width: isMobile ? 25 : 30,
                          height: isMobile ? 25 : 30,
                          cursor: "pointer",
                          transition: "background-color 0.3s ease",
                          fontSize: isMobile ? 12 : 14,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.7)")}
                        onMouseOut={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.5)")}
                      >
                        &lt;
                      </button>
                    </div>
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: 10,
                        transform: "translateY(-50%)",
                        display: "flex",
                        gap: 10,
                      }}
                    >
                      <button
                        onClick={() => handleNextImage(selectedEvent.extendedProps.id)}
                        style={{
                          background: "rgba(0,0,0,0.5)",
                          color: "white",
                          border: "none",
                          borderRadius: "50%",
                          width: isMobile ? 25 : 30,
                          height: isMobile ? 25 : 30,
                          cursor: "pointer",
                          transition: "background-color 0.3s ease",
                          fontSize: isMobile ? 12 : 14,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.7)")}
                        onMouseOut={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.5)")}
                      >
                        &gt;
                      </button>
                    </div>
                  </>
                )}
              </div>
              <div style={{
                padding: isMobile ? "20px 16px" : "30px 34px 24px",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 24 : 40
              }}>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      background: "#EFF6FF",
                      color: "#2B3A67",
                      fontWeight: 700,
                      borderRadius: 20,
                      fontSize: isMobile ? 12 : 14,
                      padding: isMobile ? "5px 12px" : "7px 18px",
                      display: "inline-block",
                      marginBottom: 16,
                    }}
                  >
                    {selectedEvent.extendedProps.status.charAt(0).toUpperCase() +
                      selectedEvent.extendedProps.status.slice(1)}
                  </div>
                  <h3
                    style={{
                      margin: "0 0 14px",
                      fontWeight: 700,
                      fontSize: isMobile ? 18 : 22,
                      color: "#1F2937",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                    title={selectedEvent.extendedProps.donorName}
                  >
                    {selectedEvent.extendedProps.donorName}
                  </h3>
                  <p
                    style={{
                      marginBottom: 8,
                      fontSize: isMobile ? 14 : 16,
                      color: "#4B5563",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Phone strokeWidth={1.8} size={isMobile ? 16 : 18} color="#374151" />
                    {selectedEvent.extendedProps.donorPhone}
                  </p>
                  <p
                    style={{
                      marginBottom: 8,
                      fontSize: isMobile ? 14 : 16,
                      color: "#4B5563",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Mail strokeWidth={1.8} size={isMobile ? 16 : 18} color="#374151" />
                    {selectedEvent.extendedProps.email || "-"}
                  </p>
                  <p
                    style={{
                      marginBottom: 8,
                      fontSize: isMobile ? 14 : 16,
                      color: "#4B5563",
                      display: "grid",
                      gridTemplateColumns: "20px 1fr",
                      alignItems: "start",
                      columnGap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      <MapPin strokeWidth={1.8} size={isMobile ? 18 : 20} color="#374151" />
                    </span>
                    <span
                      style={{
                        lineHeight: 1.45,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {selectedEvent.extendedProps.address || "No address"}
                    </span>
                  </p>
                  <p
                    style={{
                      marginBottom: 8,
                      fontSize: isMobile ? 14 : 16,
                      color: "#4B5563",
                      display: "grid",
                      gridTemplateColumns: "20px 1fr",
                      alignItems: "start",
                      columnGap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      <Building2 strokeWidth={1.8} size={isMobile ? 18 : 20} color="#374151" />
                    </span>
                    <span
                      style={{
                        lineHeight: 1.45,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      Business Name: {selectedEvent.extendedProps.businessName || "N/A"}
                    </span>
                  </p>
                  <p
                    style={{
                      marginBottom: 8,
                      fontSize: isMobile ? 14 : 16,
                      color: "#4B5563",
                      display: "grid",
                      gridTemplateColumns: "20px 1fr",
                      alignItems: "start",
                      columnGap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      <span style={{ fontSize: isMobile ? 16 : 18 }}>💰</span>
                    </span>
                    <span
                      style={{
                        lineHeight: 1.45,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {selectedEvent.extendedProps.isMonetary && selectedEvent.extendedProps.monetaryAmount > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14 }}>
                              Monetary Contribution: ${(selectedEvent.extendedProps.monetaryAmount / 100).toFixed(2)}
                            </span>
                            {(selectedEvent.extendedProps.paymentStatus || selectedEvent.extendedProps.paymentstatus) && (
                              <span style={{
                                fontSize: isMobile ? 12 : 13,
                                color:
                                  (selectedEvent.extendedProps.paymentStatus || selectedEvent.extendedProps.paymentstatus).toLowerCase() === "completed"
                                    ? "green"
                                    : "goldenrod"
                              }}>
                                (Status: {(selectedEvent.extendedProps.paymentStatus || selectedEvent.extendedProps.paymentstatus || 'Unknown')
                                  .split('_')
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' ')})
                              </span>
                            )}
                          </div>
                          {selectedEvent.extendedProps.paymentIntentId && (
                            <div style={{ fontSize: isMobile ? 12 : 13, color: "#6B7280" }}>
                              Payment ID: {selectedEvent.extendedProps.paymentIntentId}
                            </div>
                          )}
                        </div>
                      ) : (
                        "No Monetary Contribution"
                      )}
                    </span>
                  </p>

                  <p
                    style={{
                      marginBottom: 8,
                      fontSize: isMobile ? 14 : 16,
                      color: "#4B5563",
                      display: "grid",
                      gridTemplateColumns: "20px 1fr",
                      alignItems: "start",
                      columnGap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      {/* Icon placeholder */}
                    </span>

                    <span
                      style={{
                        lineHeight: 1.45,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      Pickup Type: {selectedEvent.extendedProps.pickUpType}
                    </span>

                  </p>
                </div>
                <div style={{ flex: 1, fontSize: isMobile ? 14 : 16, color: "#374151" }}>
                  <div style={{ marginBottom: 16, fontWeight: 600 }}>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ opacity: 0.7 }}>Donation Items:</span>
                      <div style={{ marginTop: 4 }}>
                        {(() => {
                          const groupedItems = new Map();
                          selectedEvent.extendedProps.donationItems.forEach((item, index) => {
                            const key = `${item.name ? item.name.toLowerCase() : ''}|${item.description ? item.description.toLowerCase() : ''}|${item.status}`;
                            if (!groupedItems.has(key)) {
                              groupedItems.set(key, {
                                ...item,
                                indices: [],
                                itemIds: [],
                                rejectionReasons: [],
                              });
                            }
                            const group = groupedItems.get(key);
                            group.indices.push(index);
                            group.itemIds.push(item.id);
                            if (item.status === 'rejected') {
                              group.rejectionReasons.push(item.rejectionReason || 'No reason provided');
                            }
                          });
                          return Array.from(groupedItems.values()).map((group) => (
                            <div
                              key={group.id}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px 0",
                                borderBottom: "1px solid #E5E7EB",
                                backgroundColor: selectedItemId === group.id ? "#F1F5F9" : "transparent",
                                cursor: "pointer",
                                borderRadius: 4,
                                flexDirection: isMobile ? "column" : "row",
                                alignItems: isMobile ? "flex-start" : "center",
                                gap: isMobile ? 8 : 0,
                              }}
                              onClick={() => handleItemClick(selectedEvent.extendedProps.id, group.id, group.indices[0])}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, color: "#1F2937", fontSize: isMobile ? 14 : 16 }}>
                                  {group.name || "Unknown"}
                                </div>
                                <div style={{ fontSize: isMobile ? 12 : 14, color: "#6B7280" }}>
                                  {group.description || "No description"}
                                </div>

                                <div style={{ fontSize: 14, color: "#6B7280" }}>
                                  Status: {group.status.charAt(0).toUpperCase() + group.status.slice(1)}
                                  {group.status === "rejected" && group.rejectionReasons.length > 0 && (
                                    <span style={{ color: "#EF4444", marginLeft: 8 }}>
                                      ({group.rejectionReasons.every(r => r === group.rejectionReasons[0]) ? group.rejectionReasons[0] : 'Various reasons'})
                                    </span>
                                  )}
                                </div>
                              </div>
                              {group.status !== "rejected" && group.status !== "completed" && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering handleItemClick
                                    handleRejectItem(selectedEvent.extendedProps.id, group.itemIds, group.name, group.indices[0]);
                                  }}
                                  style={{
                                    background: "#EF4444",
                                    color: "white",
                                    fontWeight: 500,
                                    borderRadius: 6,
                                    padding: "4px 10px",
                                    fontSize: 12,
                                    border: "none",
                                    cursor: submittingItem ? "not-allowed" : "pointer",
                                    transition: "background-color 0.3s ease",
                                    whiteSpace: "nowrap",
                                  }}
                                  disabled={submittingItem}
                                  onMouseOver={(e) => !submittingItem && (e.currentTarget.style.background = "#DC2626")}
                                  onMouseOut={(e) => !submittingItem && (e.currentTarget.style.background = "#EF4444")}
                                >
                                  {submittingItem ? "Rejecting..." : "Reject Item"}
                                </button>
                              )}
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                    <div>
                      <span style={{ opacity: 0.7 }}>Pickup Notes: </span>
                      {selectedEvent.extendedProps.note || "-"}
                    </div>
                  </div>
                  <div style={{ marginBottom: 8, display: "flex", gap: 12 }}>
                    <input
                      id={`pickup-date-${selectedEvent.extendedProps.id}`}
                      type="date"
                      value={localDateMap[selectedEvent.extendedProps.id]}
                      onChange={(e) =>
                        setLocalDateMap((m) => ({ ...m, [selectedEvent.extendedProps.id]: e.target.value }))
                      }
                      style={{
                        background: "#F9FAFB",
                        border: "1px solid #D1D5DB",
                        borderRadius: 8,
                        padding: "10px",
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#1F2937",
                        flex: 1,
                        transition: "border-color 0.3s ease",
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "#2B3A67")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "#D1D5DB")}
                    />
                    <select
                      value={localTimeMap[selectedEvent.extendedProps.id]}
                      onChange={(e) =>
                        setLocalTimeMap((m) => ({ ...m, [selectedEvent.extendedProps.id]: e.target.value }))
                      }
                      style={{
                        padding: "10px",
                        borderRadius: 8,
                        border: "1px solid #D1D5DB",
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#1F2937",
                        flex: 1,
                        transition: "border-color 0.3s ease",
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "#2B3A67")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "#D1D5DB")}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                  <select
                    id={`driver-select-${selectedEvent.extendedProps.id}`}
                    value={localDriverMap[selectedEvent.extendedProps.id]}
                    onChange={(e) =>
                      setLocalDriverMap((m) => ({ ...m, [selectedEvent.extendedProps.id]: e.target.value }))
                    }
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      border: "1px solid #D1D5DB",
                      fontWeight: 600,
                      color: "#1F2937",
                      width: "100%",
                      transition: "border-color 0.3s ease",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "#2B3A67")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "#D1D5DB")}
                  >
                    <option value="">Select Driver</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.fullName}
                      </option>
                    ))}
                  </select>
                  <div
                    style={{
                      marginTop: 32,
                      display: "flex",
                      gap: 18,
                      justifyContent: "flex-start",
                    }}
                  >
                    {selectedEvent.extendedProps.status === "pending" && (
                      <>
                        <button
                          disabled={loadingIdsLocal.includes(selectedEvent.extendedProps.id)}
                          onClick={() => handleAssign(selectedEvent)}
                          style={{
                            background: "#306fe6",
                            color: "white",
                            fontWeight: 700,
                            borderRadius: 8,
                            padding: "8px 16px",
                            fontSize: 14,
                            border: "none",
                            cursor: loadingIdsLocal.includes(selectedEvent.extendedProps.id) ? "not-allowed" : "pointer",
                            transition: "background-color 0.3s ease",
                          }}
                          onMouseOver={(e) => !loadingIdsLocal.includes(selectedEvent.extendedProps.id) && (e.currentTarget.style.background = "#1f5cd1")}
                          onMouseOut={(e) => !loadingIdsLocal.includes(selectedEvent.extendedProps.id) && (e.currentTarget.style.background = "#306fe6")}
                        >
                          {loadingIdsLocal.includes(selectedEvent.extendedProps.id) ? "Assigning..." : "Assign"}
                        </button>
                        <button
                          disabled={loadingIdsLocal.includes(selectedEvent.extendedProps.id)}
                          onClick={() => openRejectModal(selectedEvent.extendedProps.id)}
                          style={{
                            background: "#FF8200",
                            color: "white",
                            fontWeight: 700,
                            borderRadius: 8,
                            padding: "8px 16px",
                            fontSize: 14,
                            border: "none",
                            cursor: loadingIdsLocal.includes(selectedEvent.extendedProps.id) ? "not-allowed" : "pointer",
                            transition: "background-color 0.3s ease",
                          }}
                          onMouseOver={(e) => !loadingIdsLocal.includes(selectedEvent.extendedProps.id) && (e.currentTarget.style.background = "#e67600")}
                          onMouseOut={(e) => !loadingIdsLocal.includes(selectedEvent.extendedProps.id) && (e.currentTarget.style.background = "#FF8200")}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {selectedEvent.extendedProps.status === "assigned" && (
                      <>
                        <button
                          disabled={loadingIdsLocal.includes(selectedEvent.extendedProps.id)}
                          onClick={() => handleUpdate(selectedEvent)}
                          style={{
                            background: "#306fe6",
                            color: "white",
                            fontWeight: 700,
                            borderRadius: 8,
                            padding: "8px 16px",
                            fontSize: 14,
                            border: "none",
                            cursor: loadingIdsLocal.includes(selectedEvent.extendedProps.id) ? "not-allowed" : "pointer",
                            transition: "background-color 0.3s ease",
                          }}
                          onMouseOver={(e) => !loadingIdsLocal.includes(selectedEvent.extendedProps.id) && (e.currentTarget.style.background = "#1f5cd1")}
                          onMouseOut={(e) => !loadingIdsLocal.includes(selectedEvent.extendedProps.id) && (e.currentTarget.style.background = "#306fe6")}
                        >
                          {loadingIdsLocal.includes(selectedEvent.extendedProps.id) ? "Updating..." : "Update"}
                        </button>
                        <button
                          disabled={loadingIdsLocal.includes(selectedEvent.extendedProps.id)}
                          onClick={() => openRejectModal(selectedEvent.extendedProps.id)}
                          style={{
                            background: "#FF8200",
                            color: "white",
                            fontWeight: 700,
                            borderRadius: 8,
                            padding: "8px 16px",
                            fontSize: 14,
                            border: "none",
                            cursor: loadingIdsLocal.includes(selectedEvent.extendedProps.id) ? "not-allowed" : "pointer",
                            transition: "background-color 0.3s ease",
                          }}
                          onMouseOver={(e) => !loadingIdsLocal.includes(selectedEvent.extendedProps.id) && (e.currentTarget.style.background = "#e67600")}
                          onMouseOut={(e) => !loadingIdsLocal.includes(selectedEvent.extendedProps.id) && (e.currentTarget.style.background = "#FF8200")}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {selectedEvent.extendedProps.status === "completed" && (
                      <small
                        style={{
                          marginTop: 0,
                          color: "#6B7280",
                          fontWeight: 600,
                          fontSize: 16,
                          display: "block",
                        }}
                      >
                        Status: Completed
                      </small>
                    )}
                    {selectedEvent.extendedProps.status === "rejected" && (
                      <button
                        disabled={loadingIdsLocal.includes(selectedEvent.extendedProps.id)}
                        onClick={() =>
                          handleUndoRequest(
                            selectedEvent.extendedProps.id,
                            selectedEvent.extendedProps.donationItems
                          )
                        }
                        style={{
                          background: "#22C55E",
                          color: "white",
                          fontWeight: 700,
                          borderRadius: 8,
                          padding: "8px 16px",
                          fontSize: 14,
                          border: "none",
                          cursor: loadingIdsLocal.includes(selectedEvent.extendedProps.id)
                            ? "not-allowed"
                            : "pointer",
                          transition: "background-color 0.3s ease",
                        }}
                      >
                        {loadingIdsLocal.includes(selectedEvent.extendedProps.id)
                          ? "Undoing..."
                          : "Undo Reject"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )
        )}
        {rejectModal.open && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 10000,
              width: "100vw",
              height: "100vh",
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
            }}
            onClick={() => setRejectModal({ open: false, requestId: null, rejectionReason: "" })}
          >
            <div
              key="reject-modal"
              style={{
                maxWidth: 400,
                background: "#fff",
                borderRadius: 12,
                padding: "20px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                transition: "transform 0.3s ease",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: "0 0 16px", fontSize: 20, fontWeight: 600 }}>
                Reject Donation
              </h3>
              <p style={{ marginBottom: 16, color: "#4B5563" }}>
                Please provide a reason for rejecting this donation (optional):
              </p>
              <textarea
                value={rejectModal.rejectionReason}
                onChange={(e) =>
                  setRejectModal((prev) => ({ ...prev, rejectionReason: e.target.value }))
                }
                placeholder="Enter rejection reason..."
                style={{
                  width: "100%",
                  minHeight: 100,
                  padding: 10,
                  borderRadius: 8,
                  border: "1px solid #D1D5DB",
                  marginBottom: 16,
                  fontSize: 16,
                  transition: "border-color 0.3s ease",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#2B3A67")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#D1D5DB")}
              />
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  onClick={() => setRejectModal({ open: false, requestId: null, rejectionReason: "" })}
                  style={{
                    background: "#E5E7EB",
                    color: "#1F2937",
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: "10px 20px",
                    border: "none",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = "#D1D5DB")}
                  onMouseOut={(e) => (e.currentTarget.style.background = "#E5E7EB")}
                >
                  Cancel
                </button>
                <button
                  disabled={isRejecting}  // NEW: Disable during reject
                  onClick={submitReject}
                  style={{
                    background: "#EF4444",
                    color: "white",
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: "10px 20px",
                    border: "none",
                    cursor: isRejecting ? "not-allowed" : "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseOver={(e) => !isRejecting && (e.currentTarget.style.background = "#DC2626")}
                  onMouseOut={(e) => !isRejecting && (e.currentTarget.style.background = "#EF4444")}
                >
                  {isRejecting ? "Rejecting..." : "Confirm Reject"}
                </button>
              </div>
            </div>
          </div>
        )}
        {undoModal.open && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 10000,
              width: "100vw",
              height: "100vh",
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
            }}
            onClick={() => setUndoModal({ open: false, requestId: null })}
          >
            <div
              key="undo-modal"
              style={{
                maxWidth: 400,
                background: "#fff",
                borderRadius: 12,
                padding: "20px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                transition: "transform 0.3s ease",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: "0 0 16px", fontSize: 20, fontWeight: 600 }}>
                Undo Reject Donation
              </h3>
              <p style={{ marginBottom: 16, color: "#4B5563" }}>
                Are you sure you want to undo the rejection for this donation? It will revert to pending status.
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  onClick={() => setUndoModal({ open: false, requestId: null })}
                  style={{
                    background: "#E5E7EB",
                    color: "#1F2937",
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: "10px 20px",
                    border: "none",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = "#D1D5DB")}
                  onMouseOut={(e) => (e.currentTarget.style.background = "#E5E7EB")}
                >
                  Cancel
                </button>
                <button
                  disabled={isUndoing}  // NEW: Disable during undo
                  onClick={submitUndo}
                  style={{
                    background: "#22C55E",
                    color: "white",
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: "10px 20px",
                    border: "none",
                    cursor: isUndoing ? "not-allowed" : "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseOver={(e) => !isUndoing && (e.currentTarget.style.background = "#16A34A")}
                  onMouseOut={(e) => !isUndoing && (e.currentTarget.style.background = "#22C55E")}
                >
                  {isUndoing ? "Undoing..." : "Confirm Undo"}
                </button>
              </div>
            </div>
          </div>
        )}
        {rejectItemModal.open && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 10000,
              width: "100vw",
              height: "100vh",
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
            }}
            onClick={() =>
              setRejectItemModal({ open: false, requestId: null, itemIds: [], rejectionReason: "", itemName: "" })
            }
          >
            <div
              style={{
                maxWidth: 400,
                background: "#fff",
                borderRadius: 12,
                padding: "20px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                transition: "transform 0.3s ease",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: "0 0 16px", fontSize: 20, fontWeight: 600 }}>
                Reject Donation Item{rejectItemModal.itemIds.length > 1 ? 's' : ''}
              </h3>
              <p style={{ marginBottom: 16, color: "#4B5563" }}>
                Rejecting "{rejectItemModal.itemName}" ({rejectItemModal.itemIds.length} item{rejectItemModal.itemIds.length > 1 ? 's' : ''}) from donation request #{rejectItemModal.requestId}.
              </p>
              <textarea
                value={rejectItemModal.rejectionReason}
                onChange={(e) =>
                  setRejectItemModal((prev) => ({ ...prev, rejectionReason: e.target.value }))
                }
                placeholder="Enter rejection reason (optional)"
                style={{
                  width: "100%",
                  minHeight: 100,
                  padding: 10,
                  borderRadius: 8,
                  border: "1px solid #D1D5DB",
                  marginBottom: 16,
                  fontSize: 16,
                  transition: "border-color 0.3s ease",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#2B3A67")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#D1D5DB")}
              />
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  onClick={() =>
                    setRejectItemModal({
                      open: false,
                      requestId: null,
                      itemIds: [],
                      rejectionReason: "",
                      itemName: "",
                    })
                  }
                  style={{
                    background: "#E5E7EB",
                    color: "#1F2937",
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: "10px 20px",
                    border: "none",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = "#D1D5DB")}
                  onMouseOut={(e) => (e.currentTarget.style.background = "#E5E7EB")}
                >
                  Cancel
                </button>
                <button
                  disabled={submittingItem}
                  onClick={submitRejectItem}
                  style={{
                    background: "#EF4444",
                    color: "white",
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: "10px 20px",
                    border: "none",
                    cursor: submittingItem ? "not-allowed" : "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseOver={(e) => !submittingItem && (e.currentTarget.style.background = "#DC2626")}
                  onMouseOut={(e) => !submittingItem && (e.currentTarget.style.background = "#EF4444")}
                >
                  {submittingItem ? "Rejecting..." : "Confirm Reject"}
                </button>
              </div>
            </div>
          </div>
        )}
        {undoItemModal.open && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 10000,
              width: "100vw",
              height: "100vh",
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
            }}
            onClick={() =>
              setUndoItemModal({ open: false, requestId: null, itemIds: [], itemName: "" })
            }
          >
            <div
              style={{
                maxWidth: 400,
                background: "#fff",
                borderRadius: 12,
                padding: "20px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                transition: "transform 0.3s ease",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: "0 0 16px", fontSize: 20, fontWeight: 600 }}>
                Undo Reject Donation Item{undoItemModal.itemIds.length > 1 ? 's' : ''}
              </h3>
              <p style={{ marginBottom: 16, color: "#4B5563" }}>
                Undo reject for "{undoItemModal.itemName}" ({undoItemModal.itemIds.length} item{undoItemModal.itemIds.length > 1 ? 's' : ''}) from donation request #{undoItemModal.requestId}.
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  onClick={() =>
                    setUndoItemModal({
                      open: false,
                      requestId: null,
                      itemIds: [],
                      itemName: "",
                    })
                  }
                  style={{
                    background: "#E5E7EB",
                    color: "#1F2937",
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: "10px 20px",
                    border: "none",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = "#D1D5DB")}
                  onMouseOut={(e) => (e.currentTarget.style.background = "#E5E7EB")}
                >
                  Cancel
                </button>
                <button
                  disabled={submittingItem}
                  onClick={submitUndoItem}
                  style={{
                    background: "#22C55E",
                    color: "white",
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: "10px 20px",
                    border: "none",
                    cursor: submittingItem ? "not-allowed" : "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseOver={(e) => !submittingItem && (e.currentTarget.style.background = "#16A34A")}
                  onMouseOut={(e) => !submittingItem && (e.currentTarget.style.background = "#22C55E")}
                >
                  {submittingItem ? "Undoing..." : "Confirm Undo"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  function WeekView({ days: propDays } = {}) {
    const getFullWeekDays = (date) => {
      const week = [];
      const dayOfWeek = date.getDay();
      const sunday = new Date(date);
      sunday.setDate(date.getDate() - dayOfWeek);
      for (let i = 0; i < 7; i++) {
        const d = new Date(sunday);
        d.setDate(sunday.getDate() + i);
        week.push(d);
      }
      return week;
    };
    const days =
      propDays && propDays.length === 7
        ? propDays
        : getFullWeekDays(currentDate);
    const weekStatusStyles = {
      completed: { bg: "#C6F6D5", text: "#2F855A", border: "#38A169" },
      rejected: { bg: "#FED7D7", text: "#C53030", border: "#E53E3E" },
      pending: { bg: "#FEFCBF", text: "#B7791F", border: "#D69E2E" },
      assigned: { bg: "#BEE3F8", text: "#2B6CB0", border: "#3182CE" },
    };
    const cardH = isMobile ? 120 : 200;
    const imgH = isMobile ? 40 : 100;
    const titleFs = isMobile ? 10 : 16;
    const badgeFs = isMobile ? 7 : 9;
    const phoneFs = isMobile ? 9 : 13;
    const timeFs = isMobile ? 9 : 13;
    return (
      <div
        style={{
          background: calendarColors.lightBackground,
          width: "100%",
          margin: "0 auto",
          userSelect: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gridTemplateRows: "auto 1fr",
            backgroundColor: calendarColors.lightBackground,
            maxWidth: "1560px",
            margin: "0 auto",
            minHeight: isMobile ? 180 : 280,
            border: "1px solid #E5E9F2",
          }}
        >
          {days.map((day, idx) => (
            <div
              key={day.toISOString() + "-date"}
              style={{
                height: isMobile ? 40 : 50,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                borderRight: idx < 6 ? "1px solid #E5E9F2" : "none",
                padding: isMobile ? 2 : 5,
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: isMobile ? 24 : 32,
                  height: isMobile ? 24 : 32,
                  borderRadius: "50%",
                  backgroundColor: isToday(day) ? "#1E3A8A" : "transparent",
                  color: isToday(day) ? "#fff" : "#253858",
                  fontWeight: 700,
                  fontSize: isMobile ? 12 : 16,
                }}
              >
                {day.getDate()}
              </span>
              <span
                style={{
                  fontWeight: 500,
                  fontSize: isMobile ? 9 : 13,
                  color: isToday(day) ? "#1E3A8A" : "#8090B3",
                  marginTop: isMobile ? 1 : 2,
                }}
              >
                {day
                  .toLocaleDateString("en-US", { weekday: "short" })
                  .toUpperCase()}
              </span>
            </div>
          ))}
          {days.map((day, idx) => {
            const events = getEventsForDay(day);
            return (
              <div
                key={day.toISOString() + "-events"}
                className="column-scroll"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  padding: isMobile ? "4px 2px" : "8px 4px",
                  borderRight: idx < 6 ? "1px solid #E5E9F2" : "none",
                }}
              >
                {events.map((event) => {
                  const status = event.extendedProps.status || "assigned";
                  const sty = weekStatusStyles[status];
                  return (
                    <div
                      key={event.extendedProps.id}
                      onClick={() => openEventPopup(event)}
                      style={{
                        position: "relative",
                        background: "#fff",
                        borderRadius: isMobile ? 6 : 12,
                        overflow: "hidden",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        cursor: "pointer",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        borderLeft: `${isMobile ? 3 : 4}px solid ${sty.border}`,
                        width: "100%",
                        height: cardH,
                        marginBottom: isMobile ? 6 : 13,
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.15)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                      }}
                    >
                      {isMobile ? (
                        <div
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: sty.border,
                          }}
                          title={status}
                        />
                      ) : (
                        <div
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            padding: "3px 10px",
                            fontSize: badgeFs,
                            fontWeight: 600,
                            borderRadius: 20,
                            background: sty.bg,
                            color: sty.text,
                            border: `1px solid ${sty.border}`,
                          }}
                        >
                          {status.toUpperCase()}
                        </div>
                      )}
                      {event.extendedProps.donorImage ? (
                        <img
                          src={event.extendedProps.donorImage}
                          alt="donor"
                          style={{
                            width: "100%",
                            height: imgH,
                            objectFit: "cover",
                            background: "#f8f9fa",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: imgH,
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: isMobile ? 9 : 16,
                            fontWeight: 500,
                          }}
                        >
                          {isMobile ? "" : "No Image"}
                        </div>
                      )}
                      <div style={{ padding: isMobile ? 4 : 12 }}>
                        <div
                          style={{
                            fontSize: titleFs,
                            fontWeight: 600,
                            marginBottom: isMobile ? 2 : 4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {event.title}
                        </div>
                        {!isMobile && (
                          <div style={{ fontSize: phoneFs, color: "#718096", marginBottom: 6 }}>
                            {event.extendedProps.donorPhone}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: timeFs,
                            color: "#4a5568",
                            fontWeight: 500,
                            padding: isMobile ? "2px 4px" : "4px 10px",
                            backgroundColor: "#f7fafc",
                            borderRadius: isMobile ? 4 : 6,
                            display: "inline-block",
                          }}
                        >
                          {event.extendedProps.pickupTime}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function DayView() {
    const day = currentDate;
    const events = getEventsForDay(day);
    const dayStatusStyles = {
      completed: { bg: "#C6F6D5", text: "#2F855A", border: "#38A169" },
      rejected: { bg: "#FED7D7", text: "#C53030", border: "#E53E3E" },
      pending: { bg: "#FEFCBF", text: "#B7791F", border: "#D69E2E" },
      assigned: { bg: "#BEE3F8", text: "#2B6CB0", border: "#3182CE" },
    };
    const gridCols = isMobile ? "repeat(1, 1fr)" : isTablet ? "repeat(2, 1fr)" : "repeat(6, 1fr)";
    const dayImgH = isMobile ? 80 : 120;
    const dayTitleFs = isMobile ? 14 : 16;
    return (
      <div
        style={{
          background: calendarColors.lightBackground,
          width: "100%",
          margin: "0 auto",
          userSelect: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            backgroundColor: calendarColors.lightBackground,
            maxWidth: "1560px",
            margin: "0 auto",
            minHeight: isMobile ? 200 : 280,
            border: "1px solid #E5E9F2",
            padding: isMobile ? "12px" : "16px",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: isMobile ? 12 : 20 }}>
            <span
              style={{
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                width: isMobile ? 32 : 38,
                height: isMobile ? 32 : 38,
                borderRadius: "50%",
                backgroundColor: isToday(day) ? "#1E3A8A" : "transparent",
                color: isToday(day) ? "#fff" : "#253858",
                fontWeight: 700,
                fontSize: isMobile ? 16 : 18,
              }}
            >
              {day.getDate()}
            </span>
            <div
              style={{
                fontWeight: 500,
                fontSize: isMobile ? 12 : 14,
                color: isToday(day) ? "#1E3A8A" : "#8090B3",
                marginTop: 4,
              }}
            >
              {day
                .toLocaleDateString("en-US", { weekday: "short" })
                .toUpperCase()}
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              gap: isMobile ? "10px" : "16px",
            }}
          >
            {events.map((event) => {
              const status = event.extendedProps.status || "assigned";
              const sty = dayStatusStyles[status];
              return (
                <div
                  key={event.extendedProps.id}
                  onClick={() => openEventPopup(event)}
                  style={{
                    position: "relative",
                    background: "#fff",
                    borderRadius: 12,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    cursor: "pointer",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    borderLeft: `4px solid ${sty.border}`,
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.15)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      padding: "3px 10px",
                      fontSize: isMobile ? 10 : 12,
                      fontWeight: 600,
                      borderRadius: 20,
                      background: sty.bg,
                      color: sty.text,
                      border: `1px solid ${sty.border}`,
                    }}
                  >
                    {status.toUpperCase()}
                  </div>
                  {event.extendedProps.donorImage ? (
                    <img
                      src={event.extendedProps.donorImage}
                      alt="donor"
                      style={{
                        width: "100%",
                        height: dayImgH,
                        objectFit: "cover",
                        background: "#f8f9fa",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: dayImgH,
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: isMobile ? 13 : 16,
                        fontWeight: 500,
                      }}
                    >
                      No Image
                    </div>
                  )}
                  <div style={{ padding: isMobile ? 10 : 12 }}>
                    <div
                      style={{ fontSize: dayTitleFs, fontWeight: 600, marginBottom: 4 }}
                    >
                      {event.title}
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? 12 : 13,
                        color: "#718096",
                        marginBottom: 6,
                      }}
                    >
                      {event.extendedProps.donorPhone}
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? 12 : 13,
                        color: "#4a5568",
                        fontWeight: 500,
                        padding: "4px 10px",
                        backgroundColor: "#f7fafc",
                        borderRadius: 6,
                        display: "inline-block",
                      }}
                    >
                      {event.extendedProps.pickupTime}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const MonthView = () => {
    const days = getMonthDays(currentDate);
    const cellMinH = isMobile ? "60px" : "95px";
    const dayNumSize = isMobile ? 22 : 29;
    const dayNumFs = isMobile ? 12 : 16;
    const badgePad = isMobile ? "3px 6px" : "8px 14px";
    const badgeFs = isMobile ? 10 : 15;
    const badgeRadius = isMobile ? 5 : 8;
    const moreFs = isMobile ? 10 : 14;
    return (
      <div style={{ maxWidth: "100%", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            marginBottom: 4,
            marginLeft: isMobile ? 2 : 8,
            marginRight: isMobile ? 2 : 8,
          }}
        >
          {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((label) => (
            <div
              key={label}
              style={{
                color: "#98a2b3",
                fontSize: isMobile ? 10 : 13,
                letterSpacing: 0.5,
                textAlign: "center",
                paddingBottom: isMobile ? 2 : 4,
                fontWeight: 700,
              }}
            >
              {isMobile ? label.slice(0, 1) : label}
            </div>
          ))}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            background: "#fff",
            gap: "0px",
            borderTop: "1px solid #edf0fa",
            borderLeft: "1px solid #edf0fa",
          }}
        >
          {days.map((day, idx) => {
            if (!day)
              return (
                <div
                  key={idx}
                  style={{
                    minHeight: cellMinH,
                    background: "#fff",
                    borderBottom: "1px solid #edf0fa",
                    borderRight: "1px solid #edf0fa",
                  }}
                />
              );
            const events = getEventsForDay(day);
            const visibleEvent = events[0];
            const moreEvents = events.slice(1);
            const highlightCircle = isToday(day);
            return (
              <div
                key={idx}
                style={{
                  minHeight: cellMinH,
                  borderRight: "1px solid #edf0fa",
                  borderBottom: "1px solid #edf0fa",
                  padding: isMobile ? "3px 2px 0 2px" : "6px 8px 0 6px",
                  backgroundColor: (highlightCircle && isMobile) ? "rgba(30, 58, 138, 0.04)" : "#fff",
                  position: "relative",
                  color: "#2B3A67",
                  fontWeight: "normal",
                  userSelect: "none",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isMobile ? "center" : "flex-end",
                    height: isMobile ? 22 : 30,
                    marginBottom: isMobile ? 4 : 9,
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: dayNumSize,
                      height: dayNumSize,
                      borderRadius: "50%",
                      backgroundColor: highlightCircle ? "#33A7B5" : "transparent",
                      color: highlightCircle ? "#fff" : "#2B3A67",
                      textAlign: "center",
                      lineHeight: `${dayNumSize}px`,
                      fontSize: dayNumFs,
                      fontWeight: 700,
                      userSelect: "none",
                    }}
                  >
                    {day.getDate()}
                  </span>
                </div>
                {visibleEvent && (
                  <div
                    key={visibleEvent.extendedProps.id}
                    style={{
                      backgroundColor: statusStylesMonth[visibleEvent.extendedProps.status].background,
                      border: statusStylesMonth[visibleEvent.extendedProps.status].border,
                      color: "#2B3A67",
                      borderRadius: badgeRadius,
                      padding: badgePad,
                      fontWeight: 600,
                      fontSize: badgeFs,
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      marginBottom: isMobile ? 3 : 7,
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.045)",
                    }}
                    onClick={() => openEventPopup(visibleEvent)}
                    title={visibleEvent.title}
                  >
                    {visibleEvent.title}
                  </div>
                )}
                {moreEvents.length > 0 && (
                  <div
                    style={{
                      color: "#33A7B5",
                      fontWeight: 600,
                      fontSize: moreFs,
                      cursor: "pointer",
                      paddingTop: 1,
                      paddingBottom: 1,
                      marginTop: 1,
                      userSelect: "none",
                      display: "inline-block",
                    }}
                    onClick={() => openMorePopup(events)}
                  >
                    +{moreEvents.length}{isMobile ? "" : " More"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Main Calendar component wraps with minimal styling update based on your Figma
  const monthLabel = currentDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
  return (
    <div className="w-full min-h-screen bg-gray-50 flex flex-col mt-5">
      <div className="max-w-[1560px] mx-auto w-full">
        <div className="w-full bg-white flex items-center justify-between border-b border-gray-200 shadow-sm px-4 py-3 font-bold text-lg text-navy-800 tracking-wide flex-wrap overflow-x-hidden">

          <div className="flex items-center gap-2 md:gap-4">
            <button
              onClick={handlePrev}
              className="w-7 h-7 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-gray-100 border border-gray-200 text-navy-500 hover:bg-navy-50 transition-colors text-sm md:text-xl cursor-pointer"
              aria-label={currentView === "day" ? "Previous Day" : "Previous Week"}
            >
              &#60;
            </button>
            <button
              onClick={handleNext}
              className="w-7 h-7 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-gray-100 border border-gray-200 text-navy-500 hover:bg-navy-50 transition-colors text-sm md:text-xl cursor-pointer"
              aria-label={currentView === "day" ? "Next Day" : "Next Week"}
            >
              &#62;
            </button>
            <button
              onClick={handleRefresh}
              className="w-7 h-7 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-gray-100 border border-gray-200 text-navy-500 hover:bg-navy-50 transition-colors cursor-pointer"
              aria-label="Refresh Donations"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={() => setShowManualDonationForm(true)}
              title="Add Manual Donation"
              className="w-7 h-7 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-green-500 text-white shadow-md hover:bg-green-600 transition-colors cursor-pointer"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
          <span className="text-center flex-1 text-sm md:text-lg text-navy-800">
            {monthLabel}
          </span>
          <div className="flex items-center gap-3 flex-shrink-0">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-gray-50 border border-gray-200 font-semibold text-navy-700 text-xs md:text-sm cursor-pointer h-8 md:h-9"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="assigned">Assigned</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={currentView}
              onChange={(e) => setCurrentView(e.target.value)}
              className="px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-gray-50 border border-gray-200 font-semibold text-navy-700 text-xs md:text-sm cursor-pointer h-8 md:h-9"
            >
              <option value="month">Month</option>
              <option value="week">Week</option>
              <option value="day">Day</option>
            </select>
          </div>
        </div>
        <div className="flex-1 px-4 pb-4">
          {currentView === "week" && <WeekView />}
          {currentView === "day" && <DayView />}
          {currentView === "month" && <MonthView />}
        </div>
      </div>
      {!!popupEvents.length && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 overflow-y-auto p-2 sm:p-5"
          onClick={() => {
            setPopupEvents([]);
            setSelectedEvent(null);
          }}
        >
          <div
            className="max-w-[640px] w-full bg-white rounded-2xl p-4 sm:p-6 shadow-xl max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setPopupEvents([]);
                setSelectedEvent(null);
              }}
              className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors text-xl leading-none cursor-pointer"
              aria-label="Close"
            >
              ×
            </button>
            <PopupContent events={popupEvents} drivers={drivers} />
          </div>
        </div>
      )}
      {/* MANUAL DONATION MODAL */}
      {showManualDonationForm && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-5 backdrop-blur-sm"
          onClick={() => setShowManualDonationForm(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-[700px] w-full max-h-[90vh] overflow-y-auto p-8 shadow-2xl border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-7">
              <h2 className="text-2xl font-bold text-navy-800">
                Add Manual Donation
              </h2>
              <button
                onClick={() => setShowManualDonationForm(false)}
                className="text-2xl text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                ×
              </button>
            </div>

            <div className="grid gap-5">



              <div className="grid gap-4">
                <label className="input-label">Donor Name *</label>
                <input
                  placeholder="Enter donor's full name"
                  value={manualForm.donorName}
                  onChange={(e) => setManualForm({ ...manualForm, donorName: e.target.value })}
                  className="input-field"
                />

                <label className="input-label">Phone *</label>
                <input
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={manualForm.donorPhone}
                  onChange={(e) => {
                    let val = e.target.value.replace(/\D/g, "");
                    if (val && !val.startsWith("1")) val = "1" + val;
                    if (val.length > 11) val = val.slice(0, 11);
                    setManualForm({ ...manualForm, donorPhone: val ? `+${val}` : "" });
                  }}
                  className="input-field"
                />

                <label className="input-label">Email (optional)</label>
                <input
                  placeholder="Enter donor's email address"
                  value={manualForm.donorEmail}
                  onChange={(e) => setManualForm({ ...manualForm, donorEmail: e.target.value })}
                  className="input-field"
                />
                <div>
                  <label className="input-label">Donation Date *</label>
                  <input
                    type="date"
                    value={manualForm.pickupDate || ""}
                    onChange={(e) => setManualForm({ ...manualForm, pickupDate: e.target.value })}
                    className="input-field ml-2"
                    required
                  />
                </div>
                <label className="input-label">
                  Pickup Type *
                </label>

                <div className="flex gap-3">
                  {["Residential", "Corporate"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() =>
                        setManualForm({ ...manualForm, pickUpType: type })
                      }
                      className={`py-2.5 px-5 rounded-lg font-semibold cursor-pointer transition-colors ${
                        manualForm.pickUpType === type
                          ? "border-2 border-navy-500 bg-navy-50"
                          : "border border-gray-300 bg-white"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
                {manualForm.pickUpType === "Corporate" && (
                  <>
                    <label className="input-label">
                      Business Name *
                    </label>
                    <input
                      placeholder="Enter business name"
                      value={manualForm.businessName}
                      onChange={(e) =>
                        setManualForm({ ...manualForm, businessName: e.target.value })
                      }
                      className="input-field"
                    />
                  </>
                )}
                <label className="input-label">
                  Pickup Time *
                </label>

                <div className="flex gap-3">
                  {["AM", "PM"].map((time) => (
                    <button
                      key={time}
                      type="button"
                      onClick={() =>
                        setManualForm({ ...manualForm, pickUpTime: time })
                      }
                      className={`py-2.5 px-5 rounded-lg font-semibold cursor-pointer transition-colors ${
                        manualForm.pickUpTime === time
                          ? "border-2 border-green-500 bg-green-50"
                          : "border border-gray-300 bg-white"
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>

                <label className="input-label">Pickup Address</label>
                <div className="relative">
                  <input
                    placeholder="Start typing address..."
                    value={manualForm.pickupAddress}
                    onChange={async (e) => {
                      const query = e.target.value;
                      setManualForm({ ...manualForm, pickupAddress: query });
                      if (query.length < 3) {
                        setAddressSuggestions([]);
                        return;
                      }
                      try {
                        const res = await fetch(
                          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=pk.eyJ1IjoiZG9ub3JkaXNwYXRjaCIsImEiOiJjbWMwdjZrZWIwNnU2MmxwdHBsaGkzcm9rIn0.u70VsaQCG6q1r8SLZ8nVLg&country=us&limit=5`
                        );
                        const data = await res.json();
                        setAddressSuggestions(data.features || []);
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    className="input-field w-full"
                  />
                  {addressSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl max-h-[200px] overflow-y-auto z-10 shadow-lg">
                      {addressSuggestions.map((feat, i) => (
                        <div
                          key={i}
                          onClick={() => {
                            setManualForm({
                              ...manualForm,
                              pickupAddress: feat.place_name,
                            });

                            setPickupLatLng({
                              latitude: feat.center[1],
                              longitude: feat.center[0],
                            });

                            setAddressSuggestions([]);
                          }}
                          className={`px-4 py-3 cursor-pointer hover:bg-navy-50 transition-colors ${
                            i % 2 === 0 ? "bg-gray-50" : "bg-white"
                          } ${i < addressSuggestions.length - 1 ? "border-b border-gray-100" : ""}`}
                        >
                          {feat.place_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="input-label mb-3">Donated Items</label>
                {manualForm.items.map((item, idx) => (
                  <div
                    key={idx}
                    className="border-2 border-dashed border-gray-300 rounded-2xl p-5 bg-gray-50 mb-4 transition-colors relative hover:border-navy-400"
                  >
                    <input
                      placeholder="Item Name *"
                      value={item.name}
                      onChange={(e) => {
                        const newItems = [...manualForm.items];
                        newItems[idx].name = e.target.value;
                        setManualForm({ ...manualForm, items: newItems });
                      }}
                      className="input-field w-full mb-3"
                    />
                    <textarea
                      placeholder="Description (optional)"
                      value={item.description}
                      onChange={(e) => {
                        const newItems = [...manualForm.items];
                        newItems[idx].description = e.target.value;
                        setManualForm({ ...manualForm, items: newItems });
                      }}
                      rows={3}
                      className="input-field w-full mb-3 resize-y"
                    />
                    <div className="flex gap-3 items-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const newItems = [...manualForm.items];
                            newItems[idx].image = file;
                            newItems[idx].imagePreview = URL.createObjectURL(file);
                            setManualForm({ ...manualForm, items: newItems });
                          }
                        }}
                        className="p-2.5 border border-gray-300 rounded-lg bg-gray-50 cursor-pointer text-sm"
                      />
                      {item.imagePreview && (
                        <img
                          src={item.imagePreview}
                          alt="preview"
                          className="w-20 h-20 object-cover rounded-lg shadow-md"
                        />
                      )}
                    </div>
                    {manualForm.items.length > 1 && (
                      <button
                        onClick={() => setManualForm({ ...manualForm, items: manualForm.items.filter((_, i) => i !== idx) })}
                        className="absolute top-3 right-3 bg-red-100 text-red-500 border-none rounded-full px-2 py-1 text-xs cursor-pointer hover:bg-red-200 transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setManualForm({ ...manualForm, items: [...manualForm.items, { name: "", description: "", image: null, imagePreview: null }] })}
                  className="flex items-center gap-2 text-navy-500 font-semibold bg-transparent border-none py-2 cursor-pointer text-base hover:text-navy-700 transition-colors"
                >
                  + Add Another Item
                </button>
              </div>

              <div>
                <label className="input-label mb-2">Special Note (optional)</label>
                <textarea
                  placeholder="Add any special notes about the donation"
                  value={manualForm.specialNote}
                  onChange={(e) => setManualForm({ ...manualForm, specialNote: e.target.value })}
                  rows={4}
                  className="input-field w-full resize-y"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4">
              <input
                type="checkbox"
                checked={manualForm.enableReminders}
                onChange={(e) =>
                  setManualForm({
                    ...manualForm,
                    enableReminders: e.target.checked,
                  })
                }
                className="w-5 h-5 cursor-pointer accent-navy-500"
              />
              <span className="text-sm text-gray-500">
                Send reminder to donor before pickup
              </span>
            </div>

            <div className="mt-8 flex gap-3 justify-end">
              <button
                onClick={() => setShowManualDonationForm(false)}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleManualDonationSubmit}
                disabled={isSubmittingManual}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmittingManual ? "Saving..." : "Save Donation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Calendar;